import {
  CollectionDataType,
  ConfigItemCallbacks,
  ConfigItemsInfo,
} from '@ecosystem/newron-client-collections/build/shared/types';
import { Cell, ConfigItem, ConfigItemType, ConfigItemValue, Entity, PageView } from '@ecosystem/newron-client-lib';
import stringify from 'fast-json-stable-stringify';
import { DateTime } from 'luxon';
import { contextClient } from '../../client';
import { Context, FilterDefinition, FilterSections, FilterType } from '../../types';
import { InsightCollectionDataType, InsightData } from './types';
import {
  deepMergeConcat,
  isMultiCell,
  reformatCorePageSettings,
  removeContextItemsByKey,
  removeDataItemsByKey,
} from './utils';
import { queryClient } from '../../page/ReactQueryProvider';
import { getCollectionsContext } from '../../page/utils';
import { PageSettings } from '../../page/types';

const componentTypes = {
  select: ConfigItemType.Select,
  multiSelect: ConfigItemType.MultiSelect,
  dateRange: ConfigItemType.DateRange,
  numericRange: ConfigItemType.NumericRange,
};

const MARKET_CELL_KEY = 'cell';
const MARKET_TIMEFRAME_KEY = 'timeFrame';
const MARKET_DATA_TYPE_KEY = 'dataType';
const REGIONS_MULTI_CELL_ERROR = 'Regions are not available for multi-country or multi-product group markets.';
const MAX_ITEM_EDITS_WARNING = 'Feature selection is limited to 5 categories.';

const extractFilterDefs = (section: FilterSections) => {
  if (!Object.keys(section || {}).length) {
    return [];
  }

  return section.ungrouped.definitions.map((def: FilterDefinition) => {
    const { name, label, options, type, min, max } = def;
    if (type === FilterType.select || type === FilterType.multiSelect) {
      return {
        filterType: section.label,
        label,
        options,
        type: componentTypes[type],
        key: name,
      };
    } else if (type === FilterType.dateRange || type === FilterType.numericRange) {
      return {
        filterType: section.label,
        label,
        type: componentTypes[type],
        key: name,
        options: {
          min: type === FilterType.dateRange ? new Date(min) : min,
          max: type === FilterType.dateRange ? new Date(max) : max,
        },
      };
    }
  });
};

const getFilterDateValue = (date: Date) => {
  if (!date) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}${month}${day}`;
};

const getContextDateValue = (date: Date) => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${DateTime.local(2025, month, 30).monthShort} ${year}`;
};

const getLastDayOfMonth = (date: Date) => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return new Date(DateTime.local(year, month, 1).endOf('month').toFormat('yyyy-MM-dd'));
};

const getDateRange = (range, option) => {
  let minDate = range?.min ? getFilterDateValue(range.min) : '';
  const maxDate = range?.max ? getFilterDateValue(getLastDayOfMonth(range.max)) : '';
  const minContext = range?.min ? getContextDateValue(range.min) : '';
  const maxContext = range?.max ? getContextDateValue(getLastDayOfMonth(range.max)) : '';
  if (maxDate && !minDate) {
    minDate = getFilterDateValue(option.min);
  }
  const value = `${minDate}-${maxDate}`;
  let context = `${value}|${minContext}-${maxContext}`;

  if (!minContext && maxContext) {
    context = `${value}|Before ${maxContext}`;
  } else if (!maxContext && minContext) {
    context = `${value}|After ${minContext}`;
  }

  return {
    data: [value],
    context: [context],
  };
};

const getNumericRange = range => {
  const minValue = range?.min || '';
  const maxValue = range?.max || '';
  const value = `${minValue}-${maxValue}`;
  let context = `${value}|${value}`;
  if (!minValue && maxValue) {
    context = `${value}|To ${maxValue}`;
  } else if (!maxValue && minValue) {
    context = `${value}|Above ${range?.min}`;
  }
  return {
    data: [value],
    context: [context],
  };
};

const getDataContextValue = (item: ConfigItem) => {
  switch (item.type) {
    case ConfigItemType.MultiCell:
    case ConfigItemType.MultiSelect:
      return {
        data: item.value.map(row => row.value),
        context: item.value.map(row => `${row.value}|${row.label}`),
      };
    case ConfigItemType.Select:
      return {
        data: [item.value?.value],
        context: [`${item.value?.value}|${item.value?.label}`],
      };
    case ConfigItemType.DateRange:
      return getDateRange(item.value, item.options);
    case ConfigItemType.NumericRange: {
      return getNumericRange(item.value);
    }
  }
};

const getUpdatedValues = configItems => {
  const newDataChanges = {};
  const newContextChanges = {};
  configItems.forEach((item: ConfigItem) => {
    if (item.value && item.key !== MARKET_DATA_TYPE_KEY) {
      const isCellorTimeFrame = item.key === MARKET_CELL_KEY || item.key === MARKET_TIMEFRAME_KEY;

      const { data, context } = getDataContextValue(item);
      newDataChanges[item.key] = data;
      newContextChanges[isCellorTimeFrame ? item.key : `f:${item.key}`] = context;
    }
  });
  return { newDataChanges, newContextChanges };
};

const clearValues = (configItems: ConfigItem[], data, isContextItem) => {
  configItems.forEach(item => {
    if (!item.value) {
      if (isContextItem) {
        delete data[`f:${item.key}`];
      } else {
        delete data[item.key];
      }
    }
  });
  return data;
};

const getFilterValue = (item: ConfigItem, value) => {
  const range = ['min', 'max'];
  switch (item.type) {
    case ConfigItemType.MultiSelect:
      return item.options.filter(row => value.includes(row.value));
    case ConfigItemType.MultiCell:
      return item.options.filter(row => value.includes(row.value));
    case ConfigItemType.Select:
      return item.options.find(row => value.includes(row.value));
    case ConfigItemType.DateRange: {
      return value[0].split('-').reduce((acc, date, index) => {
        acc[range[index]] = date ? new Date(`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6)}`) : undefined;
        return acc;
      }, {});
    }
    case ConfigItemType.NumericRange: {
      return value[0].split('-').reduce((acc, rangeValue, index) => {
        acc[range[index]] = rangeValue;
        return acc;
      }, {});
    }
  }
};

const clearEmptyParams = paramsData => {
  Object.keys(paramsData).forEach(key => {
    if (paramsData[key][0] === '-' || paramsData[key][0] === '-|-') {
      delete paramsData[key];
    }
  });
  return paramsData;
};

const updateConfigItems = (configItems: ConfigItem[], newValue: ConfigItemValue): ConfigItem[] => {
  return configItems.map(configItem => {
    if (configItem.key === newValue.key) {
      return {
        ...configItem,
        value: newValue.value,
      } as ConfigItem;
    }
    return configItem;
  });
};

const validateMaxItemEdits = (configItems: ConfigItem[]) => {
  const maxItemEdits = 5;
  const selectedItems = configItems.filter(item => {
    if ('filterType' in item && item.filterType === 'Features' && item.value) {
      if (Array.isArray(item.value) && item.value?.length > 0) {
        return true;
      }
      if (
        typeof item.value === 'object' &&
        item.value !== null &&
        (('min' in item.value && item.value?.min) || ('max' in item.value && item.value?.max))
      ) {
        return true;
      }

      return false;
    }
  });
  if (selectedItems.length > maxItemEdits) {
    return {
      isValid: false,
      items: configItems.map(item => {
        if ('filterType' in item && item.filterType === 'Market') {
          return item;
        }
        if (selectedItems.map(it => it.key).includes(item.key)) {
          return item;
        }
        return {
          ...item,
          disabled: true,
        };
      }),
    };
  }
  return {
    isValid: true,
    items: configItems.map(item => {
      delete item.disabled;
      return item;
    }),
  };
};

export function getConfigItemCallbacks(): ConfigItemCallbacks<InsightData, object> {
  const getConfigItems = async ({
    data,
    dataType,
  }: {
    data: InsightData;
    dataType: CollectionDataType;
  }): Promise<ConfigItemsInfo> => {
    data = reformatCorePageSettings(data);
    const { paramsForContext, paramsForData, corePageSettings } = data;

    if (isMultiCell(paramsForContext)) {
      removeContextItemsByKey({ paramsForContext });
      removeDataItemsByKey({ paramsForData });
    }

    const {
      context: { url: contextUrl },
    } = corePageSettings;

    const paramsForContextKey = stringify(paramsForContext);
    const contextQuery = await queryClient.fetchQuery({
      queryKey: ['insight-context', contextUrl, paramsForContextKey],
      queryFn: () => {
        return contextClient.getContext(contextUrl, paramsForContext);
      },
      staleTime: Infinity,
    });

    const contextMarketsQuery = await queryClient.fetchQuery({
      queryKey: ['insight-context', `${contextUrl}/markets`],
      queryFn: () => {
        return contextClient.getContextSettingOptions(contextUrl + '/markets', paramsForContext);
      },
      staleTime: Infinity,
    });

    const [{ filters, settings }, marketList] = await Promise.all([contextQuery, contextMarketsQuery]);
    const filterList = [
      {
        label: MARKET_CELL_KEY,
        options: marketList,
        type: ConfigItemType.MultiCell,
        key: MARKET_CELL_KEY,
        value: settings.values.cell,
      },
      {
        type: ConfigItemType.DataTypeControl,
        key: MARKET_DATA_TYPE_KEY,
        value: dataType,
      },
      ...extractFilterDefs(filters.sections[0]),
      ...extractFilterDefs(filters.sections[1]),
    ] as ConfigItem[];

    const filterValues = filterList.map(item => {
      if (paramsForData[item.key]) {
        const value = paramsForData[item.key];
        item.value = getFilterValue(item, value);
      }
      return item;
    });

    return new Promise((resolve, _reject) => {
      resolve({
        configItems: filterValues as any,
      });
    });
  };

  const applyConfigValue = async ({
    data,
    configItems,
    newValue,
  }: {
    data: InsightData;
    configItems: ConfigItem[];
    newValue: ConfigItemValue;
  }): Promise<ConfigItemsInfo> => {
    const marketCell = configItems.find(item => item.key === MARKET_CELL_KEY);

    const marketMultiCellsWithOtherChanges =
      (marketCell.value as Cell[]).length > 1 && newValue.key !== MARKET_CELL_KEY;
    const marketMultiCellsWithNewValue = newValue.key === MARKET_CELL_KEY && (newValue.value as Cell[]).length > 1;
    const isRegionsGrouping = data.componentSettings?.grouping?.join(',')?.includes('region_name');

    if ((marketMultiCellsWithOtherChanges || marketMultiCellsWithNewValue) && isRegionsGrouping) {
      return {
        errorMessage: REGIONS_MULTI_CELL_ERROR,
        configItems: updateConfigItems(configItems, newValue) as any,
      };
    }

    if (newValue.key === MARKET_CELL_KEY) {
      data = reformatCorePageSettings(data);
      const { paramsForContext, corePageSettings } = data;

      const {
        context: { url: contextUrl },
      } = corePageSettings;

      const paramsForContextKey = stringify({
        ...paramsForContext,
        cell: (newValue.value as Entity[])?.map(row => `${row.value}|${row.label}`),
      });
      const { filters }: Context = await queryClient.fetchQuery({
        queryKey: ['insight-context', contextUrl, paramsForContextKey],
        queryFn: () => {
          return contextClient.getContext(contextUrl, {
            ...paramsForContext,
            cell: (newValue.value as Entity[])?.map(row => `${row.value}|${row.label}`),
          });
        },
        staleTime: Infinity,
      });

      const marketCell = configItems.find(item => item.key === MARKET_CELL_KEY) || {};
      const dataTypeItem = configItems.find(item => item.key === MARKET_DATA_TYPE_KEY) || {};

      const filterList = [
        marketCell,
        dataTypeItem,
        ...extractFilterDefs(filters.sections[0]),
        ...extractFilterDefs(filters.sections[1]),
      ] as ConfigItem[];

      return {
        configItems: updateConfigItems(filterList, newValue) as any,
      };
    }
    const configItemData = updateConfigItems(configItems, newValue);
    const { isValid, items } = validateMaxItemEdits(configItemData);
    if (!isValid) {
      return {
        errorMessage: MAX_ITEM_EDITS_WARNING,
        configItems: items as any,
      };
    }
    return {
      configItems: items as any,
    };
  };

  const applyMultiEditConfigValue = (args: { data: InsightData; newValue: ConfigItemValue }) => {
    const newData = {
      ...args.data,
      paramsForContext: {
        ...args.data.paramsForContext,
        cell: (args.newValue.value as unknown as Entity[]).map(row => `${row.value}|${row.label}`),
      },
      paramsForData: {
        ...args.data.paramsForData,
        cell: (args.newValue.value as unknown as Entity[]).map(row => `${row.value}`),
      },
      // For multi-edit only. Need to store old cell info for updating grouping and others details
      multiEditConfig: {
        oldCell: args.data.paramsForContext.cell,
      },
    };

    return { data: newData };
  };

  const getInsightData = async ({
    data,
    configItems,
  }: {
    data: InsightData;
    configItems: ConfigItem[];
  }): Promise<{ data: InsightData; dataType?: CollectionDataType }> => {
    const marketCell = configItems.find(item => item.key === MARKET_CELL_KEY);
    const marketDataType = configItems.find(item => item.key === MARKET_DATA_TYPE_KEY)?.value as CollectionDataType;

    const { newContextChanges, newDataChanges } = getUpdatedValues(configItems);

    const marketCellValues = marketCell?.value as ConfigItem[] | any[];

    // Check: Handle for paramsForData and paramsForContext

    let updatedParams: any = {};

    // If market cell is defined, fetch and update DSL-related values
    if (marketCellValues?.length) {
      updatedParams = await getInsightUpdatedData({ data, marketCellValues, marketDataType });

      // Inject timeFrame settings from context
      configItems = [
        ...configItems,
        {
          key: MARKET_TIMEFRAME_KEY,
          type: ConfigItemType.MultiSelect,
          value: updatedParams?.others?.timeFrameList,
        } as ConfigItem,
      ];
    }
    // Remove temparory field
    delete updatedParams?.others;

    // Assemble new data object with updated params
    const newData = {
      ...data,
      componentSettings: {
        ...data.componentSettings,
      },
      paramsForContext: clearEmptyParams({
        ...clearValues(configItems, data.paramsForContext, true),
        ...newContextChanges,
        ...updatedParams?.paramsForContext,
      }),
      paramsForData: clearEmptyParams({
        ...clearValues(configItems, data.paramsForData, false),
        ...newDataChanges,
        ...updatedParams?.paramsForData,
      }),
    };

    return { data: newData, dataType: marketDataType };
  };

  const clearConfigValues = ({
    configItems,
  }: {
    data: InsightData;
    configItems: ConfigItem[];
  }): { configItems: ConfigItem[] } | Promise<{ configItems: ConfigItem[] }> => {
    return {
      configItems: configItems.map(item => {
        if (item?.value && ![MARKET_CELL_KEY, MARKET_DATA_TYPE_KEY].includes(item.key)) {
          delete item?.value;
        }
        return item;
      }),
    };
  };

  const getViewStateInfo = ({
    data,
    pageView,
  }: {
    data: InsightData;
    pageView: Pick<PageView<{ version: number; pageSettings: PageSettings }>, 'data'>;
  }) => {
    const { paramsForContext } = data;
    const { version, pageSettings } = pageView.data;
    const { settings, filters } = getCollectionsContext({ params: { ...paramsForContext } });
    const updatedViewStateInfo = {
      viewState: {
        version: version,
        filterValues: filters,
        settingValues: settings,
        pageSettings,
      },
    };
    return updatedViewStateInfo;
  };

  const getInsightUpdatedData = async ({
    data,
    marketCellValues,
    marketDataType,
  }: {
    data: InsightData;
    marketCellValues: ConfigItem[] | any[];
    marketDataType: CollectionDataType;
  }) => {
    data = reformatCorePageSettings(data);
    const { paramsForContext, paramsForData, corePageSettings } = data;
    const {
      context: { url: contextUrl },
    } = corePageSettings;

    const cellValues = (marketCellValues as Entity[]).map(row => `${row.value}|${row.label}`);

    const paramsForContextKey = stringify({
      ...paramsForContext,
      cell: cellValues,
    });

    const { settings } = await queryClient.fetchQuery({
      queryKey: ['insight-context', contextUrl, paramsForContextKey],
      queryFn: () => {
        return contextClient.getContext(contextUrl, {
          ...paramsForContext,
          cell: cellValues,
        });
      },
      staleTime: Infinity,
    });

    // If data type is Live, then fetch latest timeFrame
    const updatedTimeframeParams = await getUpdatedContextTimeFrame({
      contextUrl,
      marketDataType,
      paramsForData,
      paramsForContext,
      settings,
      marketCellValues,
    });

    // Get updated brand options
    const updatedBrandParams = await getUpdatedBrandOptions({
      paramsForData: {
        ...paramsForData,
        timeFrame: updatedTimeframeParams?.paramsForData?.timeFrame ?? [],
      },
      paramsForContext: {
        ...paramsForContext,
        timeFrame: updatedTimeframeParams?.paramsForContext?.timeFrame ?? [],
      },
      contextUrl,
      marketCellValues,
    });

    return deepMergeConcat(updatedTimeframeParams, updatedBrandParams);
  };

  const getUpdatedContextTimeFrame = async ({
    marketDataType,
    contextUrl,
    paramsForData,
    paramsForContext,
    settings,
    marketCellValues,
  }) => {
    let latestTimeFrame: Entity[];
    const updatedParams: { paramsForData?: any; paramsForContext?: any; others?: any } = { others: {} };

    if (marketDataType === InsightCollectionDataType.live) {
      const focusType = getFocusPeriodType(paramsForContext);

      const focusPeriodParams = { ...paramsForData, cell: marketCellValues?.map(v => v.value) };

      const focusPeriods = await queryClient.fetchQuery({
        queryKey: ['insight-context', `${contextUrl}/focusPeriod`, stringify(focusPeriodParams)],
        queryFn: () => {
          return contextClient.getContextSettingOptions(
            contextUrl + '/focusPeriod',
            clearEmptyParams(focusPeriodParams)
          );
        },
        staleTime: Infinity,
      });

      const latestPeriod = getLatestFocusPeriod(focusPeriods, focusType, paramsForContext?.timeFrame);

      if (latestPeriod) latestTimeFrame = [latestPeriod];
    } else {
      const { timeFrame } = settings?.values ?? {};
      if (timeFrame) latestTimeFrame = timeFrame;
    }

    if (!latestTimeFrame || !latestTimeFrame?.length) return;

    updatedParams.others.timeFrameList = latestTimeFrame;

    updatedParams.paramsForContext = {
      ...(updatedParams?.paramsForContext ?? {}),
      timeFrame: [`${latestTimeFrame[0].value}|${latestTimeFrame[0].label}`],
    };

    updatedParams.paramsForData = {
      ...(updatedParams?.paramsForData ?? {}),
      timeFrame: [`${latestTimeFrame[0].value}`],
    };

    return updatedParams;
  };

  const getFocusPeriodType = params => {
    if (!params?.timeFrame?.length) return null;

    // Example: "Monthly:661-667:Custom|Jan - Jul 2025"
    const [, , typePart] = params.timeFrame[0].split(':');
    if (!typePart) return null;

    // typePart might look like "Custom|Jan - Jul 2025"
    const focusType = typePart.split('|')[0].trim();

    // Normalize mapping
    const typeMap = {
      Week: 'Week',
      Month: 'Month',
      Quarter: 'Quarter',
      RollingQuarter: 'RollingQuarter',
      Year: 'Year',
      MAT: 'MAT',
      YTD: 'YTD',
      Custom: 'Custom',
    };

    return typeMap[focusType] || focusType;
  };

  const getLatestFocusPeriod = (focusPeriods, type = null, selectedTimeFrame) => {
    if (!Array.isArray(focusPeriods) || focusPeriods.length === 0) return null;

    const getType = value => {
      const [, , typePart] = value.split(':');
      return typePart?.split('|')[0].trim();
    };

    const parseSeqRange = value => {
      const parts = value.split(':')[1];
      return parts ? parts.split('-').map(Number) : [];
    };

    const getLabelBySeq = seq => {
      const fp = focusPeriods.find(f => parseSeqRange(f.value).includes(seq));
      if (!fp) return null;
      const [, , typePart] = fp.value.split(':');
      return typePart?.split('|')[1] || fp.label || null;
    };

    const handleCustom = () => {
      const selected = selectedTimeFrame?.[0];
      if (!selected) return null;

      const seqs = parseSeqRange(selected);
      if (seqs.length === 0) return null;

      const span = seqs.length === 2 ? seqs[1] - seqs[0] + 1 : 1;
      const isMonthly = selected.startsWith('Monthly');
      const typeKey = isMonthly ? 'Month' : 'Week';

      const allOfType = focusPeriods
        .filter(fp => getType(fp.value) === typeKey)
        .sort((a, b) => Math.max(...parseSeqRange(b.value)) - Math.max(...parseSeqRange(a.value)));

      if (allOfType.length === 0) return null;

      const latestEndSeq = Math.max(...parseSeqRange(allOfType[0].value));
      const newStartSeq = latestEndSeq - (span - 1);

      const startLabel = getLabelBySeq(newStartSeq);
      const endLabel = getLabelBySeq(latestEndSeq);

      if (!startLabel || !endLabel) {
        return { value: selected, label: selected.split('|')[1] || 'Custom' };
      }

      return {
        value: `${isMonthly ? 'Monthly' : 'Weekly'}:${newStartSeq}-${latestEndSeq}:Custom`,
        label: `${startLabel} - ${endLabel}`,
      };
    };

    if (type === 'Custom') return handleCustom();

    const filtered = type ? focusPeriods.filter(fp => getType(fp.value) === type) : focusPeriods;
    if (filtered.length === 0) return null;

    return filtered.reduce((latest, current) => {
      const latestSeq = Math.max(...parseSeqRange(latest.value));
      const currentSeq = Math.max(...parseSeqRange(current.value));
      return currentSeq > latestSeq ? current : latest;
    });
  };

  const getUpdatedBrandOptions = async ({ paramsForData, paramsForContext, contextUrl, marketCellValues }) => {
    const brandOptionsParams = {
      ...paramsForData,
      cell: marketCellValues?.map(v => v.value),
    };

    const brandOptionsRes = await queryClient.fetchQuery({
      queryKey: ['insight-context', `${contextUrl}/brandOptions`, stringify(brandOptionsParams)],
      queryFn: () =>
        contextClient.getContextSettingOptions(`${contextUrl}/brandOptions`, clearEmptyParams(brandOptionsParams)),
      staleTime: Infinity,
    });

    const brandOptions = ((brandOptionsRes[0] ?? {}) as any)?.options ?? [];

    /** ---------- helpers ---------- */

    const getParentMatches = (group, highlightedBrandParam) => {
      return highlightedBrandParam.includes(group.value) ? [group] : [];
    };

    const getChildMatches = (group, highlightedBrandParam) => {
      if (!group.options?.length) return [];
      return group.options.filter(opt => highlightedBrandParam.includes(opt.value));
    };

    const collectMatches = (brandOptions, highlightedBrandParam) => {
      return brandOptions.flatMap(group => [
        ...getParentMatches(group, highlightedBrandParam),
        ...getChildMatches(group, highlightedBrandParam),
      ]);
    };

    const getHighlightedBrand = (brandOptions, highlightedBrandParam) => {
      if (!brandOptions?.length) return null;

      const selected = collectMatches(brandOptions, highlightedBrandParam);

      return selected.length > 0 ? selected : [brandOptions[0]];
    };

    const shouldResetCompetitors = (brandOptions, competitorBrandListParam, competitorBrandListContext) => {
      if (!competitorBrandListContext?.length) return false;

      const selected = brandOptions.filter(opt => competitorBrandListParam.includes(opt.value));

      return !selected?.length || selected.length !== competitorBrandListParam?.length;
    };

    const highlightedBrand = getHighlightedBrand(brandOptions, paramsForData.highlightedBrand);

    const resetCompetitors = shouldResetCompetitors(
      brandOptions,
      paramsForData.competitorBrandList,
      paramsForContext.competitorBrandList
    );

    const buildContextParams = (context, highlightedBrand, resetCompetitors) => {
      return {
        highlightedBrand: highlightedBrand
          ? highlightedBrand.map(hb => `${hb.value}|${hb.label}`)
          : context.highlightedBrand,
        competitorBrandList: resetCompetitors ? [] : (context.competitorBrandList ?? []),
        competitorBrandOption: resetCompetitors ? ['none|No Competitors'] : (context.competitorBrandOption ?? []),
        isCompetitorBrandOwner: resetCompetitors ? [] : (context.isCompetitorBrandOwner ?? []),
        brandCompetitorCategory: resetCompetitors ? [] : (context.brandCompetitorCategory ?? []),
        analyseOption: resetCompetitors ? [] : (context.analyseOption ?? []),
      };
    };

    const buildDataParams = (data, highlightedBrand, resetCompetitors) => {
      return {
        highlightedBrand: highlightedBrand ? highlightedBrand.map(hb => hb.value) : data.highlightedBrand,
        competitorBrandList: resetCompetitors ? [] : (data.competitorBrandList ?? []),
        competitorBrandOption: resetCompetitors ? ['none'] : (data.competitorBrandOption ?? []),
        isCompetitorBrandOwner: resetCompetitors ? [] : (data.isCompetitorBrandOwner ?? []),
        brandCompetitorCategory: resetCompetitors ? [] : (data.brandCompetitorCategory ?? []),
      };
    };

    return {
      paramsForContext: buildContextParams(paramsForContext, highlightedBrand, resetCompetitors),
      paramsForData: buildDataParams(paramsForData, highlightedBrand, resetCompetitors),
    };
  };

  return {
    getConfigItems,
    applyConfigValue,
    getViewStateInfo,
    applyMultiEditConfigValue,
    getInsightData,
    clearConfigValues: clearConfigValues as any,
  };
}
